/**
 * Life4Earth V2 — Chat API Adapter
 *
 * REST API functions for the chat system.
 * All functions use apiFetch() which handles auth and base URL.
 */
import { apiFetch } from './client';

/* ------------------------------------------------------------------ */
/*  Types (mirror backend DTOs)                                        */
/* ------------------------------------------------------------------ */

export interface ContactResponse {
  id: string;
  requesterGuid: string;
  recipientGuid: string;
  status: string;
  requestedAt: string;
  respondedAt: string | null;
  displayName: string | null;
  screenName: string | null;
  profileImageUrl: string | null;
}

export interface ParticipantResponse {
  userGuid: string;
  role: string;
  status: string;
  joinedAt: string | null;
  isMuted: boolean;
  displayName: string | null;
  screenName: string | null;
  profileImageUrl: string | null;
}

export interface ConversationResponse {
  id: string;
  conversationType: string;
  name: string | null;
  /** DEV-1391 — custom group avatar URL. null = derive from participants. */
  avatarUrl: string | null;
  projectGuid: string | null;
  createdBy: string;
  isActive: boolean;
  isArchived: boolean;
  isPinned: boolean;
  lastMessageAt: string | null;
  createdAt: string;
  unreadCount: number;
  lastMessagePreview: string | null;
  participants: ParticipantResponse[];
}

export interface ReactionSummary {
  emoji: string;
  count: number;
  currentUserReacted: boolean;
}

export interface MessageAttachment {
  id: string;
  fileKey: string;
  fileName: string;
  fileSizeBytes: number;
  contentType: string;
  thumbnailKey?: string;
}

export interface MessageResponse {
  id: string;
  conversationId: string;
  senderGuid: string;
  content: string | null;
  messageType: string;
  isEdited: boolean;
  editedAt: string | null;
  isDeleted: boolean;
  isPinned: boolean;
  createdAt: string;
  senderDisplayName: string | null;
  senderScreenName: string | null;
  senderProfileImageUrl: string | null;
  reactions: ReactionSummary[];
  attachments: MessageAttachment[];
}

/* ------------------------------------------------------------------ */
/*  Contacts                                                           */
/* ------------------------------------------------------------------ */

export async function fetchContacts(): Promise<ContactResponse[]> {
  return apiFetch<ContactResponse[]>('/api/contacts');
}

export async function fetchPendingRequests(): Promise<ContactResponse[]> {
  return apiFetch<ContactResponse[]>('/api/contacts/pending');
}

/**
 * DEV-1485 — pending OUTGOING requests (ones I sent, awaiting their answer).
 *
 * The server has had GET /api/contacts/sent since the contacts feature landed;
 * nothing on the client ever called it, so a request you sent simply had no
 * surface to appear on. `/pending` is INCOMING only (recipient = me), which is
 * why "I requested contact and nothing showed up" looked like a write failure
 * when the row was there all along.
 */
export async function fetchSentRequests(): Promise<ContactResponse[]> {
  return apiFetch<ContactResponse[]>('/api/contacts/sent');
}

export async function sendContactRequest(recipientGuid: string): Promise<ContactResponse> {
  return apiFetch<ContactResponse>('/api/contacts/request', {
    method: 'POST',
    body: JSON.stringify({ recipientGuid }),
  });
}

export async function acceptContact(contactId: string): Promise<ContactResponse> {
  return apiFetch<ContactResponse>(`/api/contacts/${contactId}/accept`, {
    method: 'PUT',
  });
}

export async function rejectContact(contactId: string): Promise<void> {
  return apiFetch<void>(`/api/contacts/${contactId}/reject`, {
    method: 'PUT',
  });
}

export async function removeContact(contactId: string): Promise<void> {
  return apiFetch<void>(`/api/contacts/${contactId}`, { method: 'DELETE' });
}

export interface ContactStatusResponse {
  status: 'none' | 'pending' | 'accepted' | 'rejected' | 'blocked' | 'blocked_by_me' | 'blocked_by_them';
  contactId: string | null;
  direction: 'incoming' | 'outgoing' | null;
}

export async function fetchContactStatus(userGuid: string): Promise<ContactStatusResponse> {
  return apiFetch<ContactStatusResponse>(`/api/contacts/status/${userGuid}`);
}

export async function blockUser(userGuid: string, reason?: string): Promise<void> {
  return apiFetch<void>('/api/contacts/block', {
    method: 'POST',
    body: JSON.stringify({ userGuid, reason }),
  });
}

export async function unblockUser(userGuid: string): Promise<void> {
  return apiFetch<void>(`/api/contacts/block/${userGuid}`, {
    method: 'DELETE',
  });
}

/* ── Contact search ─────────────────────────────────────────────── */

export interface ContactSearchResult {
  userGuid: string;
  displayName: string;
  screenName: string | null;
  profileImageUrl: string | null;
  email: string | null;
  contactId: string;
}

/**
 * Search accepted contacts by name, screen name, or email.
 * Server-side filtered — only returns users who are bilateral contacts.
 */
export async function searchContacts(
  query: string,
  limit = 10
): Promise<ContactSearchResult[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  return apiFetch<ContactSearchResult[]>(`/api/contacts/search?${params}`);
}

/* ------------------------------------------------------------------ */
/*  Conversations                                                      */
/* ------------------------------------------------------------------ */

export interface CreateConversationRequest {
  conversationType: 'direct' | 'group' | 'project_team';
  name?: string;
  projectGuid?: string;
  participantGuids: string[];
}

export async function fetchConversations(): Promise<ConversationResponse[]> {
  return apiFetch<ConversationResponse[]>('/api/conversations');
}

export async function fetchArchivedConversations(): Promise<ConversationResponse[]> {
  return apiFetch<ConversationResponse[]>('/api/conversations/archived');
}

export async function fetchConversation(id: string): Promise<ConversationResponse> {
  return apiFetch<ConversationResponse>(`/api/conversations/${id}`);
}

export async function createConversation(req: CreateConversationRequest): Promise<ConversationResponse> {
  return apiFetch<ConversationResponse>('/api/conversations', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

// DEV-1391 — accepts partial updates ({ name } and/or { avatarUrl }). Empty
// string avatarUrl clears the custom avatar (server reverts to derived).
export async function updateConversation(
  id: string,
  updates: { name?: string; avatarUrl?: string | null }
): Promise<ConversationResponse> {
  return apiFetch<ConversationResponse>(`/api/conversations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function archiveConversation(id: string): Promise<void> {
  return apiFetch<void>(`/api/conversations/${id}/archive`, { method: 'PUT' });
}

export async function unarchiveConversation(id: string): Promise<void> {
  return apiFetch<void>(`/api/conversations/${id}/unarchive`, { method: 'PUT' });
}

export async function leaveConversation(id: string): Promise<void> {
  return apiFetch<void>(`/api/conversations/${id}/leave`, { method: 'PUT' });
}

export async function inviteParticipant(conversationId: string, userGuid: string): Promise<void> {
  return apiFetch<void>(`/api/conversations/${conversationId}/participants`, {
    method: 'POST',
    body: JSON.stringify({ userGuid }),
  });
}

export async function removeParticipant(conversationId: string, userGuid: string): Promise<void> {
  return apiFetch<void>(`/api/conversations/${conversationId}/participants/${userGuid}`, {
    method: 'DELETE',
  });
}

export async function changeParticipantRole(
  conversationId: string,
  userGuid: string,
  role: 'admin' | 'member'
): Promise<void> {
  const url = `/api/conversations/${conversationId}/participants/${userGuid}/role`;
  const body = { role };
  console.log('[changeParticipantRole] Calling:', { url, body });
  try {
    const result = await apiFetch<void>(url, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    console.log('[changeParticipantRole] Success:', result);
    return result;
  } catch (err: any) {
    console.error('[changeParticipantRole] Failed:', { url, body, error: err.message || err });
    throw err;
  }
}

export async function pinConversation(conversationId: string): Promise<{ isPinned: boolean }> {
  return apiFetch<{ isPinned: boolean }>(`/api/conversations/${conversationId}/participants/me/pin`, {
    method: 'PUT',
  });
}

export async function muteConversation(conversationId: string, isMuted: boolean): Promise<void> {
  return apiFetch<void>(`/api/conversations/${conversationId}/mute`, {
    method: 'PUT',
    body: JSON.stringify({ isMuted }),
  });
}

/* ------------------------------------------------------------------ */
/*  Messages                                                           */
/* ------------------------------------------------------------------ */

export async function fetchMessages(
  conversationId: string,
  before?: string,
  limit = 50
): Promise<MessageResponse[]> {
  const params = new URLSearchParams();
  if (before) params.set('before', before);
  params.set('limit', String(limit));
  return apiFetch<MessageResponse[]>(
    `/api/conversations/${conversationId}/messages?${params}`
  );
}

export async function sendMessage(
  conversationId: string,
  content: string,
  messageType = 'text',
  attachmentFileKeys: string[] = []
): Promise<MessageResponse> {
  return apiFetch<MessageResponse>(
    `/api/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      body: JSON.stringify({ content, messageType, attachmentFileKeys }),
    }
  );
}

export async function editMessage(
  conversationId: string,
  messageId: string,
  content: string
): Promise<MessageResponse> {
  return apiFetch<MessageResponse>(
    `/api/conversations/${conversationId}/messages/${messageId}`,
    {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }
  );
}

export async function deleteMessage(conversationId: string, messageId: string): Promise<void> {
  return apiFetch<void>(
    `/api/conversations/${conversationId}/messages/${messageId}`,
    { method: 'DELETE' }
  );
}

export async function togglePin(conversationId: string, messageId: string): Promise<{ isPinned: boolean }> {
  return apiFetch<{ isPinned: boolean }>(
    `/api/conversations/${conversationId}/messages/${messageId}/pin`,
    { method: 'PUT' }
  );
}

export async function fetchPinnedMessages(conversationId: string): Promise<MessageResponse[]> {
  return apiFetch<MessageResponse[]>(
    `/api/conversations/${conversationId}/messages/pinned`
  );
}

export async function toggleReaction(
  conversationId: string,
  messageId: string,
  emoji: string
): Promise<{ added: boolean; reactions: ReactionSummary[] }> {
  return apiFetch<{ added: boolean; reactions: ReactionSummary[] }>(
    `/api/conversations/${conversationId}/messages/${messageId}/reactions`,
    {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }
  );
}

export async function reportMessage(
  conversationId: string,
  messageId: string,
  reason: string,
  description?: string
): Promise<void> {
  return apiFetch<void>(
    `/api/conversations/${conversationId}/messages/${messageId}/report`,
    {
      method: 'POST',
      body: JSON.stringify({ reason, description }),
    }
  );
}

export async function searchMessages(
  conversationId: string,
  query: string,
  limit = 20
): Promise<MessageResponse[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  return apiFetch<MessageResponse[]>(
    `/api/conversations/${conversationId}/messages/search?${params}`
  );
}

/* ------------------------------------------------------------------ */
/*  Notifications                                                      */
/* ------------------------------------------------------------------ */

export interface NotificationApiResponse {
  id: string;
  recipientGuid: string;
  actorGuid: string | null;
  type: string;
  title: string;
  body: string | null;
  href: string | null;
  referenceId: string | null;
  referenceType: string | null;
  metadata: string | null;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
  actorDisplayName: string | null;
  actorProfileImageUrl: string | null;
}

export interface NotificationCountResponse {
  unread: number;
  total: number;
}

export interface NotificationPreferencesResponse {
  notifyChatMessages: boolean;
  notifyContactRequests: boolean;
  notifyContactAccepted: boolean;
  notifyApprovals: boolean;
  notifyProjectUpdates: boolean;
  notifySystem: boolean;
  emailDigest: string;
}

export async function fetchNotifications(
  limit = 50,
  before?: string,
  unreadOnly = false
): Promise<NotificationApiResponse[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) params.set('before', before);
  if (unreadOnly) params.set('unreadOnly', 'true');
  return apiFetch<NotificationApiResponse[]>(`/api/notifications?${params}`);
}

export async function fetchNotificationCount(): Promise<NotificationCountResponse> {
  return apiFetch<NotificationCountResponse>('/api/notifications/count');
}

export async function markNotificationRead(id: string): Promise<void> {
  return apiFetch<void>(`/api/notifications/${id}/read`, { method: 'PUT' });
}

export async function markAllNotificationsRead(): Promise<void> {
  return apiFetch<void>('/api/notifications/read-all', { method: 'PUT' });
}

export async function dismissNotification(id: string): Promise<void> {
  return apiFetch<void>(`/api/notifications/${id}`, { method: 'DELETE' });
}

export async function fetchNotificationPreferences(): Promise<NotificationPreferencesResponse> {
  return apiFetch<NotificationPreferencesResponse>('/api/notifications/preferences');
}

export async function updateNotificationPreferences(
  prefs: Partial<NotificationPreferencesResponse>
): Promise<NotificationPreferencesResponse> {
  return apiFetch<NotificationPreferencesResponse>('/api/notifications/preferences', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

/* ── Role Invitation Response ──────────────────────────────────────── */

export async function respondToRoleInvitation(
  assignmentGuid: string,
  action: 'accept' | 'decline',
  note?: string
): Promise<{ message: string; status: string }> {
  return apiFetch<{ message: string; status: string }>(
    `/api/role-invitations/${assignmentGuid}/respond`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, note: note || undefined }),
    }
  );
}
